// GAP 2 (SMALL-WAVE completeness pass) — ported 1:1, byte-for-byte, from
// apps/web/src/lib/resumeToDocx.ts. Unlike resumeToPrint.ts's port (which
// needed print-safe --tl-* var pinning because ResumeView's screen styles
// route through this app's theme vars), this file has ZERO app-specific
// styling: docx's own Paragraph/TextRun color literals (INK/MUTED/ACCENT/
// RULE below) are the .docx document's OWN theme, entirely independent of
// either app's --tl-*/ink-* CSS — so no adaptation was needed, only the
// import path (this app's own @careeropt/schema alias, same package).
//
// Maps a ResumeDocument → a real, editable, ATS-clean .docx (selectable text,
// no images). Pure client util. The `docx` library runs in the browser;
// Packer.toBlob → download. Styling here is independent of ResumeView's CSS
// (separate renderer, same model — the WHAT/HOW seam).
//
// TEMPLATES-P2 (docs/design/resume-template-library-2026-07-05.md §8's
// per-family export-path matrix: ".docx … keyed styles (add the Letter/A4
// locale the current A4-hardcode lacks)"): resumeToDocxBlob now accepts an
// OPTIONAL `family`/`accentKey` pair. Omitted (every pre-existing caller):
// byte-identical to before — A4, the original blue ACCENT, original section
// order. Named: applies the family's derived page geometry (§9 — Letter for
// us_standard, A4 otherwise, FIXING the prior always-A4 hardcode), the
// resolved accent hex (§10, ATS-Plain forced to Graphite), and the family's
// display section order (§3's view-swap, e.g. Recent Graduate's education-
// first sequence) — the SAME three axes the screen renderer and the react-pdf
// builder apply, so all three surfaces agree on what a given family means.

import {
  BorderStyle,
  Document,
  Packer,
  PageOrientation,
  Paragraph,
  TabStopPosition,
  TabStopType,
  TextRun,
  convertMillimetersToTwip,
} from 'docx';
import type { ResumeDocument, ResumeSection } from '@careeropt/schema';
import {
  applyTemplateDisplayOrder,
  effectiveAccent,
  pageGeometryForVariant,
  type ResumeTemplateFamily,
} from './resumeTemplateRegistry';

const INK = '1A1F29';
const MUTED = '5B6472';
/** The original, pre-templates default accent — kept as the fallback for
 *  every caller that omits `family` (byte-identical legacy behavior). */
const DEFAULT_ACCENT = '1D4ED8';
const RULE = 'D6DAE1';

/** Strip a leading '#' from a resolved accent hex (react-pdf/CSS use '#…',
 *  docx's own color literals are bare 6-digit hex with no '#'). */
function docxHex(hex: string): string {
  return hex.startsWith('#') ? hex.slice(1) : hex;
}

function fmtDate(d?: string | null): string {
  if (!d) return '';
  const m = /^(\d{4})-(\d{2})/.exec(d);
  if (!m) return d;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[Number(m[2]) - 1] ?? ''} ${m[1]}`.trim();
}

function sectionHeading(text: string, accent: string): Paragraph {
  return new Paragraph({
    spacing: { before: 220, after: 90 },
    border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: RULE, space: 2 } },
    children: [
      new TextRun({ text: text.toUpperCase(), bold: true, size: 18, color: accent, allCaps: true }),
    ],
  });
}

function bullet(text: string, accent: string, kpi?: string | null): Paragraph {
  const runs = [new TextRun({ text, size: 21, color: INK })];
  if (kpi) runs.push(new TextRun({ text: `  (${kpi})`, size: 21, color: accent, bold: true }));
  return new Paragraph({
    spacing: { after: 40 },
    indent: { left: 220, hanging: 160 },
    children: [new TextRun({ text: '▪  ', size: 16, color: accent }), ...runs],
  });
}

function renderSection(section: ResumeSection, accent: string): Paragraph[] {
  const out: Paragraph[] = [sectionHeading(section.heading, accent)];
  switch (section.kind) {
    case 'profile':
      out.push(new Paragraph({ children: [new TextRun({ text: section.body, size: 21, color: INK })] }));
      break;
    case 'experience':
      for (const r of section.roles) {
        const dates = [fmtDate(r.startDate), r.isCurrent ? 'Present' : fmtDate(r.endDate)]
          .filter(Boolean)
          .join(' – ');
        out.push(
          new Paragraph({
            spacing: { before: 120, after: 0 },
            tabStops: [{ type: TabStopType.RIGHT, position: TabStopPosition.MAX }],
            children: [
              new TextRun({ text: r.title, bold: true, size: 21, color: INK }),
              new TextRun({ text: `, ${r.company}`, size: 21, color: INK }),
              ...(r.location ? [new TextRun({ text: `  ${r.location}`, size: 19, color: MUTED })] : []),
              new TextRun({ text: `\t${dates}`, size: 19, color: MUTED }),
            ],
          }),
        );
        if (r.descriptor) {
          out.push(
            new Paragraph({
              spacing: { after: 40 },
              children: [new TextRun({ text: r.descriptor, italics: true, size: 19, color: MUTED })],
            }),
          );
        }
        for (const b of r.bullets) out.push(bullet(b.text, accent, b.kpi));
      }
      break;
    case 'skills':
      for (const g of section.groups) {
        out.push(
          new Paragraph({
            spacing: { after: 30 },
            children: [
              new TextRun({ text: `${g.category}: `, bold: true, size: 21, color: INK }),
              new TextRun({ text: g.items.join(', '), size: 21, color: INK }),
            ],
          }),
        );
      }
      break;
    case 'education':
      for (const e of section.items) {
        const dates = [fmtDate(e.startDate), fmtDate(e.endDate)].filter(Boolean).join(' – ');
        out.push(
          new Paragraph({
            spacing: { after: 30 },
            children: [
              new TextRun({ text: e.institution, bold: true, size: 21, color: INK }),
              ...(e.credential ? [new TextRun({ text: ` — ${e.credential}`, size: 21, color: INK })] : []),
              ...(e.field ? [new TextRun({ text: `, ${e.field}`, size: 19, color: MUTED })] : []),
              ...(dates ? [new TextRun({ text: `  (${dates})`, size: 19, color: MUTED })] : []),
            ],
          }),
        );
      }
      break;
    case 'certifications':
      for (const c of section.items) {
        out.push(
          new Paragraph({
            spacing: { after: 30 },
            children: [
              new TextRun({ text: c.name, bold: true, size: 21, color: INK }),
              ...(c.issuer ? [new TextRun({ text: ` — ${c.issuer}`, size: 19, color: MUTED })] : []),
              ...(c.issuedDate ? [new TextRun({ text: `  (${fmtDate(c.issuedDate)})`, size: 19, color: MUTED })] : []),
            ],
          }),
        );
      }
      break;
    case 'selection_criteria':
      for (const r of section.responses) {
        out.push(
          new Paragraph({
            spacing: { before: 120, after: 30 },
            children: [new TextRun({ text: r.criterion, bold: true, size: 21, color: INK })],
          }),
        );
        out.push(
          new Paragraph({
            children: [
              new TextRun({
                text: r.body || 'Draft your response to this criterion.',
                italics: !r.body,
                size: 21,
                color: r.body ? INK : MUTED,
              }),
            ],
          }),
        );
      }
      break;
  }
  return out;
}

/**
 * Build the .docx Blob for a served ResumeDocument. `family`/`accentKey` are
 * OPTIONAL — every pre-existing caller (omitting both) gets EXACTLY today's
 * output: A4, the original blue accent, original section order. Passing
 * `family` applies that family's derived page size (§9 — Letter for a
 * us_standard document, fixing the prior always-A4 hardcode), resolved
 * accent (§10), and display section order (§3), the same three axes the
 * screen renderer and the react-pdf builder read from the SAME registry.
 */
export async function resumeToDocxBlob(
  doc: ResumeDocument,
  family?: ResumeTemplateFamily,
  accentKey?: string | null,
): Promise<Blob> {
  const displayDoc = family ? applyTemplateDisplayOrder(doc, family) : doc;
  const { contact } = displayDoc;
  const accent = family ? docxHex(effectiveAccent(family, accentKey).hex) : DEFAULT_ACCENT;
  const geometry = family ? pageGeometryForVariant(displayDoc.formatVariant) : { widthMm: 210, heightMm: 297 };
  const contactBits = [contact.location, contact.email, contact.phone, ...(contact.links?.map((l) => l.label) ?? [])]
    .filter(Boolean)
    .join('  ·  ');

  const children: Paragraph[] = [
    new Paragraph({
      spacing: { after: 20 },
      children: [new TextRun({ text: contact.name || 'Your Name', bold: true, size: 44, color: INK })],
    }),
    new Paragraph({
      spacing: { after: 60 },
      children: [new TextRun({ text: contactBits, size: 19, color: MUTED })],
    }),
    ...displayDoc.sections.flatMap((s) => renderSection(s, accent)),
  ];

  const document = new Document({
    creator: 'Career Optimizer',
    styles: {
      default: {
        document: { run: { font: 'Calibri' } },
      },
    },
    sections: [
      {
        properties: {
          page: {
            size: {
              orientation: PageOrientation.PORTRAIT,
              width: convertMillimetersToTwip(geometry.widthMm),
              height: convertMillimetersToTwip(geometry.heightMm),
            },
            margin: {
              top: convertMillimetersToTwip(16),
              bottom: convertMillimetersToTwip(16),
              left: convertMillimetersToTwip(18),
              right: convertMillimetersToTwip(18),
            },
          },
        },
        children,
      },
    ],
  });

  return Packer.toBlob(document);
}

/** Generate + trigger a .docx download in the browser. */
export async function downloadResumeDocx(
  doc: ResumeDocument,
  filename: string,
  family?: ResumeTemplateFamily,
  accentKey?: string | null,
): Promise<void> {
  const blob = await resumeToDocxBlob(doc, family, accentKey);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.docx') ? filename : `${filename}.docx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke after a tick so the download can start.
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
